import { useQrScanner } from '@/features/scanner/hooks/useQrScanner';
import { Spinner } from '@/shared/components/Spinner';
import './QrScannerView.css';

interface QrScannerViewProps {
  enabled: boolean;
  onDecode: (rawValue: string) => void;
}

export function QrScannerView({ enabled, onDecode }: QrScannerViewProps) {
  const { videoRef, status, errorMessage, torchSupported, torchOn, toggleTorch } = useQrScanner({
    onDecode,
    enabled
  });

  return (
    <div className="qr-scanner">
      {/* The <video> element is always mounted (rather than conditionally
          rendered) so videoRef is attached before getUserMedia resolves --
          conditionally mounting it would race the camera-start effect. */}
      <video
        ref={videoRef}
        className="qr-scanner__video"
        playsInline
        muted
        aria-hidden="true"
        style={{ visibility: status === 'active' ? 'visible' : 'hidden' }}
      />

      {status === 'active' && (
        <>
          <div className="qr-scanner__reticle" aria-hidden="true" />
          <p className="qr-scanner__hint">Align the QR code within the frame</p>

          {torchSupported && (
            <button
              type="button"
              className={`qr-scanner__torch${torchOn ? ' qr-scanner__torch--on' : ''}`}
              onClick={toggleTorch}
              aria-label={torchOn ? 'Turn off flashlight' : 'Turn on flashlight'}
              aria-pressed={torchOn}
            >
              ⚡
            </button>
          )}
        </>
      )}

      {status === 'starting' && (
        <div className="qr-scanner__status">
          <Spinner label="Starting camera" />
          <span>Starting camera…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="qr-scanner__status">
          <span aria-hidden="true" style={{ fontSize: '1.75rem' }}>
            📷
          </span>
          <span role="alert">{errorMessage}</span>
        </div>
      )}
    </div>
  );
}
